import { useEffect, useRef, useState } from "react"
import type { ComponentPropsWithoutRef, SyntheticEvent } from "react"
import { t } from "@lingui/core/macro"
import {
    addIgnoredEmoticon,
    removeIgnoredEmoticon,
    useMessageRenderOptionsQuery,
} from "../../../store/chatSlice"
import { useAppDispatch } from "../../../store/hooks"
import { CloseThick } from "../../common/atoms/Icons/Chat"
import { sendEmoticonReport } from "../ReportMessage/api"
import { classNames } from "../utils"
import styles from "./EmoticonDetails.module.scss"
import type { IEmoticon } from "../RenderedMessage/utils"

interface EmoticonDetailsProps extends ComponentPropsWithoutRef<"div"> {
    emoticon: IEmoticon
    onClose: () => void
}

enum ReportState {
    default = "default",
    report = "report",
    undo = "undo",
}

export function EmoticonDetails({ emoticon, onClose }: EmoticonDetailsProps) {
    const dispatch = useAppDispatch()
    const { data: messageRenderOptions } = useMessageRenderOptionsQuery()
    const ignoredEmoticons = messageRenderOptions?.ignored_emoticons ?? []
    const [reportState, setReportState] = useState<ReportState>(
        ReportState.default,
    )
    const reportOptionsList = [
        { label: t`Just ignore this emoticon`, value: "ignore" },
        { label: t`Offensive`, value: "offensive" },
        { label: t`Disgusting`, value: "disgusting" },
        { label: t`Fake Tip`, value: "fake_tip" },
        { label: t`Advertising`, value: "advertising" },
    ]
    const selectRef = useRef<HTMLSelectElement>(null)
    const src = emoticon.imgUrl || emoticon.thumbUrl
    const reportOptions = reportOptionsList.map((option) => (
        <option key={option.value} value={option.value}>
            {option.label}
        </option>
    ))
    const report = () => {
        const reportOption = selectRef.current?.value || ""
        sendEmoticonReport(emoticon.name, reportOption).then(() => {
            setReportState(ReportState.undo)
            if (reportOption === "ignore") {
                dispatch(addIgnoredEmoticon(emoticon.name))
            }
        })
    }
    const undo = () => {
        const reportOption = selectRef.current?.value || ""
        sendEmoticonReport(emoticon.name, reportOption).then(() => {
            setReportState(ReportState.default)
            if (reportOption === "ignore") {
                dispatch(removeIgnoredEmoticon(emoticon.name))
            }
        })
    }
    const absorb = (e: SyntheticEvent) => {
        e.stopPropagation()
    }

    useEffect(() => {
        if (ignoredEmoticons.includes(emoticon.name)) {
            setReportState(ReportState.undo)
        }
    }, [ignoredEmoticons, emoticon?.name])

    return (
        <div className={styles.emoticonDetails} onClick={onClose}>
            <div className={styles.container} onClick={absorb}>
                <button
                    className={styles.closeX}
                    onClick={onClose}
                    data-testid="note-close-button"
                >
                    <CloseThick height={12} width={12} />
                </button>
                <img className={styles.image} alt={emoticon.name} src={src} />
                <button
                    className={classNames(styles.reportLink, {
                        [styles.hidden]: reportState !== ReportState.default,
                    })}
                    onClick={() => setReportState(ReportState.report)}
                >
                    {t`REPORT EMOTICON`}
                </button>
                <div
                    className={classNames(styles.reportWrapper, {
                        [styles.hidden]: reportState !== ReportState.report,
                    })}
                >
                    <div>
                        <span
                            className={styles.categoryLabel}
                        >{t`Choose a category:`}</span>
                        <select
                            className={styles.categorySelect}
                            ref={selectRef}
                        >
                            {reportOptions}
                        </select>
                    </div>
                    <div className={styles.buttonWrapper}>
                        <button
                            className={styles.cancelButton}
                            onClick={() => setReportState(ReportState.default)}
                        >
                            {t`CANCEL`}
                        </button>
                        <button
                            className={styles.reportButton}
                            onClick={() => report()}
                        >
                            {t`REPORT`}
                        </button>
                    </div>
                </div>
                <div
                    className={classNames(styles.undoWrapper, {
                        [styles.hidden]: reportState !== ReportState.undo,
                    })}
                >
                    <span>{t`EMOTICON REPORTED - `}</span>
                    <button
                        className={styles.undoButton}
                        onClick={() => undo()}
                    >
                        {t`UNDO`}
                    </button>
                </div>
            </div>
        </div>
    )
}
